import React, {
  Suspense,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  lazy,
} from "react";

import AuthService from "../../utilities/services/auth.service";

import { useModal } from "../ModalProvider/ModalProvider";

import { timezones, toUtcIsoFromTimezone } from "../../utilities/helpers";

import { getOrgVenues, createNewSeason } from "../../utilities/api";

import { CreateSeasonModal } from "./CreateSeasonModal";
import { Success } from "../../components/ModalContent";

// Create the context
const CreateSeasonContext = createContext(undefined);

export const useSeasonConfig = () => {
  const context = useContext(CreateSeasonContext);
  if (!context) {
    throw new Error("Context must be used within a useSeasonConfig");
  }
  return context;
};

export const CreateSeasonProvider = ({ children }) => {
  const { openModal, closeModal } = useModal();

  const [showSeason, setSeasonShow] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [venues, setVenues] = useState();

  const [room, setRoom] = useState({ uuid: "" });

  const [startDate, setStartDate] = useState();

  const [endDate, setEndDate] = useState();

  const [valid, setValid] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    details: "",
    room: "",
  });

  const [initialState, setInitialState] = useState();

  const [isSaving, setIsSaving] = useState(false);

  const [alert, setAlert] = useState({
    show: false,
    variant: "",
    message: "",
  });

  const organization = AuthService.getOrg() ? AuthService.getOrg()[0] : "";

  useEffect(() => {
    if (AuthService.getOrg() == undefined) return;
    setInitialState({ formData, startDate, endDate, room });
  }, []);

  useEffect(() => {
    if (formData?.name && room && startDate && endDate) {
      setValid(true);
    } else {
      setValid(false);
    }
  }, [formData, startDate, endDate, room]);

  useEffect(() => {
    // Close alert if inputs change
    if (alert.show) {
      closeAlert();
    }
  }, [formData, room, startDate, endDate]);

  const loadVenues = () => {
    setIsLoading(true);
    getOrgVenues(
      typeof organization === "object" && organization?.uuid
        ? organization.uuid
        : undefined,
    )
      .then((res) => {
        setVenues(res?.data);
        console.log("Venues: ", res?.data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
      });
  };

  const handleVenue = (e) => {
    const uuid = e.target.value;
    const timezone = venues?.find((venue) =>
      venue.rooms.find((room) => room.uuid === uuid),
    )?.timezone;
    const venue = venues?.find((venue) =>
      venue.rooms.find((room) => room.uuid === uuid),
    );
    const room = venue.rooms.find((room) => room.uuid === uuid);
    setRoom({ ...room, timezone });
  };

  const closeAlert = () => {
    setAlert({
      show: false,
      variant: "",
      message: "",
    });
  };

  const handleSeasonClose = () => {
    setSeasonShow(false);
  };

  // reset state
  const handleSeasonShow = () => {
    setSeasonShow(true);
    setFormData(initialState?.formData);
    setRoom(initialState?.room);
    setEndDate(initialState?.endDate);
    setStartDate(initialState?.startDate);
    setValid(false);
  };

  const handleSubmit = () => {
    setIsSaving(true);
    const timezone = timezones(room?.timezone);
    let data = {};
    data["name"] = formData?.name.trim();
    data["details"] = formData?.details;
    data["room"] = room;
    data["starts"] = toUtcIsoFromTimezone(startDate, timezone);
    data["ends"] = toUtcIsoFromTimezone(endDate, timezone);
    data["organizationUUID"] = organization?.uuid;

    createNewSeason({ data })
      .then((res) => {
        handleSeasonClose();
        openModal(
          Success,
          {
            title: "Season created successfully",
            onClose: () => closeModal(),
          },
          { className: "modal-md", contentClassName: "" },
        );
      })
      .catch((err) => {
        console.error(err);
        setAlert({
          show: true,
          variant: "danger",
          message: `Unable to create season. Please try again.`,
        });
      })
      .finally(() => setIsSaving(false));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <CreateSeasonContext.Provider
      value={{
        showSeason,
        handleSeasonShow,
      }}
    >
      <CreateSeasonModal
        showSeason={showSeason}
        loadVenues={loadVenues}
        isLoading={isLoading}
        venues={venues}
        room={room}
        formData={formData}
        handleVenue={handleVenue}
        handleClose={handleSeasonClose}
        isSaving={isSaving}
        handleChange={handleChange}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        startDate={startDate}
        endDate={endDate}
        valid={valid}
        alert={alert}
        handleSubmit={handleSubmit}
      />
      {children}
    </CreateSeasonContext.Provider>
  );
};
